import { useMemo, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { useUsers } from "@/hooks/useUsers";
import { AssignedUser, User } from "@/types";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";

interface UserComboboxProps {
  value?: string;
  onChange: (pid: string) => void;
  /** Pre-selected user so the label renders before the search query resolves */
  initialUser?: AssignedUser | null;
  placeholder?: string;
  disabled?: boolean;
  /** Show an inline clear button when a user is selected */
  clearable?: boolean;
}

/** Searchable single-user picker (server search, 300ms debounce). */
export function UserCombobox({
  value,
  onChange,
  initialUser,
  placeholder = "Select user…",
  disabled,
  clearable,
}: UserComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // The user picked in this session; a later search page may not include them.
  const [picked, setPicked] = useState<User | null>(null);
  const debouncedSearch = useDebounce(search, 300);
  const { data: usersResponse, isLoading } = useUsers(debouncedSearch, 1, 20);

  const options = useMemo(() => {
    const map = new Map<string, User>();
    if (initialUser?.pid) map.set(initialUser.pid, initialUser as User);
    if (picked) map.set(picked.pid, picked);
    (usersResponse?.data ?? []).forEach((u) => map.set(u.pid, u));
    return Array.from(map.values());
  }, [initialUser, picked, usersResponse?.data]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (u) => u.fullName.toLowerCase().includes(q) || (u.email?.toLowerCase().includes(q) ?? false),
    );
  }, [options, search]);

  const selected = options.find((u) => u.pid === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", !value && "text-muted-foreground")}
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <UserAvatar src={selected.avatarUrl} name={selected.fullName} className="h-6 w-6 shrink-0" fallbackClassName="text-[10px]" />
              <span className="truncate">{selected.fullName}</span>
            </span>
          ) : (
            placeholder
          )}
          <span className="ml-2 flex shrink-0 items-center gap-1">
            {clearable && value && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear selection"
                className="rounded-sm opacity-50 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                onClick={(e) => { e.stopPropagation(); setPicked(null); onChange(""); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setPicked(null); onChange(""); } }}
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search people…" value={search} onValueChange={setSearch} />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : visible.length === 0 ? (
              <CommandEmpty>{debouncedSearch ? "No users found." : "Type to search."}</CommandEmpty>
            ) : (
              <CommandGroup>
                {visible.map((user) => (
                  <CommandItem
                    key={user.pid}
                    value={user.pid}
                    onSelect={() => { setPicked(user); onChange(user.pid); setOpen(false); }}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <UserAvatar src={user.avatarUrl} name={user.fullName} className="h-6 w-6 shrink-0" fallbackClassName="text-[10px]" />
                      <span className="flex min-w-0 flex-col truncate">
                        <span className="truncate text-sm">{user.fullName}</span>
                        {user.email && <span className="truncate text-[10px] text-muted-foreground">{user.email}</span>}
                      </span>
                    </span>
                    <Check className={cn("h-4 w-4 shrink-0", value === user.pid ? "opacity-100" : "opacity-0")} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
